import { type ReactNode, type RefObject, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../cn.ts';
import { Button } from './Button.tsx';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Usually action buttons. Rendered at the bottom. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Width preset. Default `md` (28rem). */
  size?: 'sm' | 'md' | 'lg';
  /** Close on Escape and on backdrop click. Default true; turn off while saving. */
  dismissible?: boolean;
  /** Element to focus first; defaults to the first focusable element in the dialog. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
}

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' };

/**
 * Modal dialog (WAI-ARIA dialog pattern): labelled by its title, focus moved in and trapped,
 * Escape/backdrop close it, focus returns to the opener, page scroll is locked while open.
 * Visuals follow the dashboard's components-next/dialog/Dialog.vue.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  size = 'md',
  dismissible = true,
  initialFocusRef,
  className,
}: DialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const onOpenChangeRef = useRef(onOpenChange);
  const dismissibleRef = useRef(dismissible);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
    dismissibleRef.current = dismissible;
  });

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Prefer the first control in the content over the close button in the header.
    const target =
      initialFocusRef?.current ??
      content.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current;
    target?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissibleRef.current) {
        event.stopPropagation();
        onOpenChangeRef.current(false);
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:pt-[clamp(2rem,10vh,6rem)]">
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-n-alpha-black1 backdrop-blur-[4px]"
        onClick={() => dismissible && onOpenChange(false)}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full flex flex-col gap-6 p-6 rounded-xl shadow-xl animate-dialog-in text-start',
          'bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container focus:outline-none',
          WIDTHS[size],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2 min-w-0">
            <h2 id={titleId} className="text-base font-medium leading-6 text-n-slate-12 m-0">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="text-sm text-n-slate-11 m-0">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              aria-label="Close"
              icon={<X className="size-4" />}
              onClick={() => onOpenChange(false)}
            />
          )}
        </div>
        <div ref={content} className="contents">
          {children}
          {footer && <div className="flex items-center justify-end gap-3">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
